import {
  Button,
  Calendar,
  Card,
  CardContent,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { CalendarIcon, Loader2 } from "@/components/icons";
import { Field } from "@/components/setup/field";
import { StepFooter, StepHeading } from "@/components/setup/step-chrome";
import type { SetupStepProps } from "@/components/setup/steps";
import { checkMySlug, createMySite, updateMySite } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { currentDateFnsLocale } from "@/lib/locale-format";
import { formatLocaleLabelWithTag } from "@/lib/locales";
import { uploadsOpenUntil } from "@/lib/setup-dates";
import { isValidSlug, slugify } from "@/lib/slug";
import { uploaderDisplayPath } from "@/lib/uploader-url";

/** Buyer locale ("cs"/"en") → best matching uploader variant. */
function defaultVariant(orderLocale: string, variants: string[]): string {
  const match = variants.find((v) =>
    v.toLowerCase().startsWith(orderLocale.toLowerCase()),
  );
  return match ?? variants[0] ?? "en-US";
}

function toDateString(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/**
 * Wizard step 1 — the essentials: how the couple's names appear, when
 * the wedding is, the page's web address, and its language. Creates
 * the site on first save; later saves patch only what changed.
 */
export function StepBasics({
  orderId,
  setup,
  linear,
  onDone,
  onBack,
}: SetupStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { site, order, variants } = setup;

  const [displayName, setDisplayName] = useState(site?.displayName ?? "");
  const [weddingDate, setWeddingDate] = useState<string>(
    site?.weddingDate ?? "",
  );
  const [slug, setSlug] = useState(site?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(site !== null);
  const [language, setLanguage] = useState(
    (setup.config?.language) ??
      defaultVariant(order.locale, variants),
  );
  const [slugError, setSlugError] = useState<string | null>(null);

  // Suggest the address from the names until the buyer edits it.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(displayName));
  }, [displayName, slugTouched]);

  // Live availability, debounced; skipped when unchanged on re-entry.
  const [debouncedSlug, setDebouncedSlug] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSlug(slug), 400);
    return () => clearTimeout(id);
  }, [slug]);
  const slugFormatValid = isValidSlug(slug);
  const availability = useQuery({
    queryKey: ["checkMySlug", debouncedSlug],
    queryFn: () => checkMySlug({ slug: debouncedSlug }),
    enabled:
      slugFormatValid &&
      debouncedSlug.length > 0 &&
      debouncedSlug === slug &&
      debouncedSlug !== site?.slug,
    staleTime: 30_000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (site) {
        const patch: Record<string, string> = {};
        if (displayName !== site.displayName) patch.displayName = displayName;
        if (slug !== site.slug) patch.slug = slug;
        if (weddingDate !== site.weddingDate) patch.weddingDate = weddingDate;
        if (Object.keys(patch).length > 0) {
          await updateMySite({ siteId: site.siteId, patch });
        }
        return;
      }
      await createMySite({
        orderId,
        displayName,
        slug,
        weddingDate,
        language,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mySetup", orderId] });
      onDone();
    },
    onError: (err) => {
      const appErr = toAppError(err);
      if (appErr.isSlugError) {
        setSlugError(t("setup.basics.slugTaken"));
      } else {
        toast.error(appErr.message);
      }
    },
  });

  const save = () => {
    setSlugError(null);
    if (!displayName.trim()) {
      toast.error(t("setup.basics.namesRequired"));
      return;
    }
    if (!weddingDate) {
      toast.error(t("setup.basics.dateRequired"));
      return;
    }
    if (!slugFormatValid) {
      setSlugError(t("setup.basics.slugInvalidFormat"));
      return;
    }
    if (availability.data && !availability.data.available) {
      setSlugError(
        availability.data.reason === "taken"
          ? t("setup.basics.slugTaken")
          : t("setup.basics.slugUnusable"),
      );
      return;
    }
    mutation.mutate();
  };

  const untilDate =
    weddingDate &&
    format(uploadsOpenUntil(weddingDate, order.snapshot.months), "PPP", {
      locale: currentDateFnsLocale(),
    });

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <StepHeading step="basics" />

        <Field
          label={t("setup.basics.namesLabel")}
          htmlFor="setup-names"
          description={t("setup.basics.namesDesc")}
        >
          <Input
            id="setup-names"
            autoComplete="off"
            placeholder="Zuzka & Lukáš"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-11 text-base"
          />
        </Field>

        <Field
          label={t("setup.basics.dateLabel")}
          description={t("setup.basics.dateDesc")}
        >
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full justify-start text-base font-normal"
              >
                <CalendarIcon />
                {weddingDate
                  ? format(new Date(`${weddingDate}T12:00:00`), "PPP", {
                      locale: currentDateFnsLocale(),
                    })
                  : t("setup.basics.pickDate")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={
                  weddingDate
                    ? new Date(`${weddingDate}T12:00:00`)
                    : undefined
                }
                onSelect={(d) => d && setWeddingDate(toDateString(d))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {untilDate && (
            <p className="text-sm text-muted-foreground">
              <Trans
                i18nKey="setup.basics.uploadsUntil"
                values={{
                  date: untilDate,
                  months: t("orderStatus.months", { count: order.snapshot.months ?? 1 }),
                }}
                components={{ b: <strong /> }}
              />
            </p>
          )}
        </Field>

        <Field
          label={t("setup.basics.addressLabel")}
          htmlFor="setup-slug"
          description={t("setup.basics.addressDesc")}
        >
          <Input
            id="setup-slug"
            autoComplete="off"
            spellCheck={false}
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase());
              setSlugError(null);
            }}
            className="h-11 font-mono text-base"
          />
          <p className="text-sm text-muted-foreground">
            <Trans
              i18nKey="setup.basics.guestsVisit"
              values={{ path: uploaderDisplayPath(slug || t("setup.basics.slugExample")) }}
              components={{ c: <code className="font-mono text-foreground" /> }}
            />
          </p>
          {slugError ? (
            <p className="text-sm text-destructive">{slugError}</p>
          ) : slugFormatValid && debouncedSlug === slug &&
            debouncedSlug !== site?.slug ? (
            availability.isFetching ? (
              <p className="flex items-center gap-1 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> {t("setup.basics.checking")}
              </p>
            ) : availability.data ? (
              availability.data.available ? (
                <p className="text-sm text-emerald-600 dark:text-emerald-500">
                  {t("setup.basics.addressFree")}
                </p>
              ) : (
                <p className="text-sm text-destructive">
                  {availability.data.reason === "taken"
                    ? t("setup.basics.slugTaken")
                    : t("setup.basics.slugUnusable")}
                </p>
              )
            ) : null
          ) : null}
        </Field>

        {!site && (
          <Field
            label={t("setup.basics.languageLabel")}
            description={t("setup.basics.languageDesc")}
          >
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="h-11 w-full text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(variants.length > 0 ? variants : [language]).map((v) => (
                  <SelectItem key={v} value={v}>
                    {formatLocaleLabelWithTag(v)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        <StepFooter onBack={onBack}>
          <Button
            type="button"
            size="lg"
            onClick={save}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />{" "}
                {site ? t("common.saving") : t("setup.basics.creating")}
              </>
            ) : linear ? (
              t("setup.saveContinue")
            ) : (
              t("common.save")
            )}
          </Button>
        </StepFooter>
      </CardContent>
    </Card>
  );
}
